import type { NavItem, NavSection } from "@majitap/ui";
import {
  Banknote,
  BellDot,
  BellRing,
  Boxes,
  Building2,
  CalendarRange,
  ClipboardCheck,
  ClipboardPen,
  FileText,
  Gauge,
  HandCoins,
  Handshake,
  Inbox,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Map as MapIcon,
  MapPin,
  Package,
  PackageCheck,
  Palette,
  Plug,
  Receipt,
  Route,
  ScrollText,
  Settings,
  Tag,
  Ticket,
  Timer,
  TriangleAlert,
  Truck,
  UserCheck,
  UserRound,
  Users,
  Wallet,
  Webhook,
} from "lucide-react";

/**
 * Who sees an item: "anyone" (staff in the company), or the permission(s), any of which will do.
 * Each gate is the permission the page's list endpoint checks, so an item shows exactly when the
 * page can load. "anyone" lists have no permission check; their actions keep theirs.
 */
export type Gate = "anyone" | string | readonly string[];

export interface ConsoleItem {
  id: string;
  label: string;
  path: string;
  icon: NavItem["icon"];
  gate: Gate;
  /** The page header's one line. */
  description: string;
}

export interface ConsoleSection {
  id: string;
  label?: string;
  items: ConsoleItem[];
}

/** The main menu: the nav table in the shell spec (`Chlwa` in docs/design/mothership.pen). */
export const MAIN_MENU: ConsoleSection[] = [
  {
    id: "home",
    items: [
      {
        id: "overview",
        label: "Overview",
        path: "/",
        icon: LayoutDashboard,
        gate: "anyone",
        description: "What needs attention today.",
      },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      {
        id: "orders",
        label: "Orders",
        path: "/orders",
        icon: Package,
        gate: "orders.read",
        description: "Every order, its price and its deliveries.",
      },
      {
        id: "deliveries",
        label: "Deliveries",
        path: "/deliveries",
        icon: PackageCheck,
        gate: "orders.read",
        description: "Every trip, on any day: where it stands, who carries it, what it holds.",
      },
      {
        id: "dispatch",
        label: "Dispatch",
        path: "/dispatch",
        icon: Route,
        gate: "deliveries.dispatch",
        description: "Assign fill sites, trucks and drivers to today's deliveries.",
      },
      {
        id: "plan",
        label: "Plan the week",
        path: "/plan",
        icon: CalendarRange,
        gate: "deliveries.dispatch",
        description: "Assign a week of deliveries at once, days ahead, and see each fill site's expected use.",
      },
      {
        id: "incidents",
        label: "Incidents",
        path: "/incidents",
        icon: TriangleAlert,
        gate: "deliveries.dispatch",
        description: "Problems on deliveries, open and resolved.",
      },
    ],
  },
  {
    id: "customers",
    label: "Customers",
    items: [
      {
        id: "customers",
        label: "Customers",
        path: "/customers",
        icon: Users,
        gate: "anyone",
        description: "People who order water, their locations and tanks.",
      },
      {
        id: "businesses",
        label: "Businesses",
        path: "/businesses",
        icon: Building2,
        gate: "anyone",
        description: "Business accounts, their members and credit.",
      },
      {
        id: "sensors",
        label: "Tank sensors",
        path: "/sensors",
        icon: Gauge,
        gate: "customers.manage",
        description: "Tank level sensors, and the tanks running low.",
      },
    ],
  },
  {
    id: "network",
    label: "Network",
    items: [
      {
        id: "fleet",
        label: "Fleet",
        path: "/fleet",
        icon: Truck,
        gate: "anyone",
        description: "Trucks, drivers, assistants, fill sites and shifts.",
      },
      {
        id: "live-map",
        label: "Live map",
        path: "/fleet/map",
        icon: MapPin,
        gate: "anyone",
        description: "Where the trucks on shift are now, from their drivers' phones.",
      },
      {
        id: "partners",
        label: "Partners",
        path: "/partners",
        icon: Handshake,
        gate: "partners.manage",
        description: "Third-party suppliers: onboarding, members and payout details.",
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    items: [
      {
        id: "invoices",
        label: "Invoices",
        path: "/invoices",
        icon: FileText,
        gate: "invoices.issue",
        description: "Invoices, payment terms and what is overdue.",
      },
      {
        id: "payments",
        label: "Payments",
        path: "/payments",
        icon: Banknote,
        gate: "payments.record",
        description: "Payments in, till payments to match, and refunds.",
      },
      {
        id: "wallets",
        label: "Wallets",
        path: "/wallets",
        icon: Wallet,
        gate: "payments.record",
        description: "Customer, business, driver and partner wallets.",
      },
      {
        id: "payouts",
        label: "Payouts",
        path: "/payouts",
        icon: HandCoins,
        gate: "payouts.approve",
        description: "Payout requests to approve and pay.",
      },
    ],
  },
  {
    id: "catalog",
    label: "Catalog & pricing",
    items: [
      {
        id: "products",
        label: "Products",
        path: "/products",
        icon: Boxes,
        gate: "anyone",
        description: "What MajiTap sells.",
      },
      {
        id: "pricing",
        label: "Pricing",
        path: "/pricing",
        icon: Tag,
        gate: "pricing.manage",
        description: "Price rules, charge types and the quote tool.",
      },
      {
        id: "vouchers",
        label: "Vouchers",
        path: "/vouchers",
        icon: Ticket,
        gate: "pricing.manage",
        description: "Discount vouchers.",
      },
      {
        id: "zones",
        label: "Service zones",
        path: "/zones",
        icon: MapIcon,
        gate: "anyone",
        description: "The zones MajiTap delivers in.",
      },
    ],
  },
];

/** The Settings menu (`BH45D`): it replaces the main menu under /settings. */
export const SETTINGS_MENU: ConsoleSection[] = [
  {
    id: "team",
    label: "Team",
    items: [
      {
        id: "staff",
        label: "Staff & roles",
        path: "/settings/staff",
        icon: Users,
        gate: "staff.manage",
        description: "Invite staff, give them roles, and resend an invitation when someone can't sign in.",
      },
      {
        id: "alerts",
        label: "Alerts",
        path: "/settings/alerts",
        icon: BellDot,
        gate: "staff.manage",
        description: "Who hears about new orders, failed deliveries, payments and problems, by SMS, email or here.",
      },
      {
        id: "audit",
        label: "Audit log",
        path: "/settings/audit",
        icon: ScrollText,
        gate: "staff.manage",
        description: "Who changed what, and when.",
      },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    items: [
      {
        id: "shift-checklist",
        label: "Shift checklist",
        path: "/settings/shift-checklist",
        icon: ClipboardCheck,
        gate: "fleet.manage",
        description: "What drivers answer to start a shift: photos, checks and readings.",
      },
      {
        id: "proof-checklist",
        label: "Delivery proof",
        path: "/settings/proof-checklist",
        icon: ClipboardPen,
        gate: "fleet.manage",
        description: "What drivers answer to complete a delivery: photos, a signature, checks and notes.",
      },
      {
        id: "dispatch-alerts",
        label: "Dispatch alerts",
        path: "/settings/dispatch-alerts",
        icon: BellRing,
        gate: "fleet.manage",
        description: "When Dispatch flags a delivery, and how near drivers must be to mark that they've arrived.",
      },
      {
        id: "delivery-eta",
        label: "Delivery ETA",
        path: "/settings/delivery-eta",
        icon: Timer,
        gate: "fleet.manage",
        description: "How long a delivery on its way will take: whether customers see it, and how it's worked out.",
      },
      {
        id: "status-reasons",
        label: "Status reasons",
        path: "/settings/status-reasons",
        icon: ListChecks,
        gate: "orders.write",
        description: "What staff pick when they cancel an order, or cancel, suspend or reroute a delivery.",
      },
    ],
  },
  {
    id: "connections",
    label: "Connections",
    items: [
      {
        id: "integrations",
        label: "Integrations",
        path: "/settings/integrations",
        icon: Plug,
        gate: "integrations.manage",
        description: "M-Pesa, SMS, email, QuickBooks and KRA eTIMS for this company.",
      },
      {
        id: "accounting",
        label: "Accounting & tax",
        path: "/settings/accounting",
        icon: Receipt,
        gate: "integrations.manage",
        description: "Product mappings and syncing to QuickBooks and eTIMS.",
      },
    ],
  },
  {
    id: "developers",
    label: "Developers",
    items: [
      {
        id: "api-tokens",
        label: "API tokens",
        path: "/settings/api-tokens",
        icon: KeyRound,
        gate: "integrations.manage",
        description: "Tokens businesses and partners use for the API.",
      },
      {
        id: "webhooks",
        label: "Webhooks",
        path: "/settings/webhooks",
        icon: Webhook,
        gate: "integrations.manage",
        description: "Webhook endpoints and their deliveries.",
      },
      {
        id: "failed-events",
        label: "Failed events",
        path: "/settings/failed-events",
        icon: Inbox,
        gate: "integrations.manage",
        description: "Events that ran out of retries, to send again.",
      },
    ],
  },
  {
    id: "cut-over",
    label: "Cut-over",
    items: [
      {
        id: "readiness",
        label: "Sign-in readiness",
        path: "/settings/readiness",
        icon: UserCheck,
        gate: "readiness.manage",
        description: "Who has signed in to the new system, and reminders for who hasn't.",
      },
    ],
  },
  {
    id: "company",
    label: "Company",
    items: [
      {
        id: "branding",
        label: "Branding",
        path: "/settings/branding",
        icon: Palette,
        gate: "company.manage",
        description: "The company's logos: the square mark in the sidebar, and the full one for documents.",
      },
      {
        id: "invoice-details",
        label: "Invoice details",
        path: "/settings/invoice-details",
        icon: FileText,
        gate: "company.manage",
        description:
          "What invoices say about the company: its legal name and KRA PIN, who to contact, and how to pay it.",
      },
    ],
  },
];

/** My profile (P8-54): every staff member's own, in the user menu rather than the sidebar. */
export const ACCOUNT_ITEM: ConsoleItem = {
  id: "account",
  label: "My profile",
  path: "/account",
  icon: UserRound,
  gate: "anyone",
  description: "Your name, photo and password, and how the console looks in this browser.",
};

/** Settings, pinned above the user: shown when any Settings page is. */
export const SETTINGS_ITEM: ConsoleItem = {
  id: "settings",
  label: "Settings",
  path: "/settings",
  icon: Settings,
  gate: SETTINGS_MENU.flatMap((s) => s.items).flatMap((i) => (typeof i.gate === "string" ? [i.gate] : [...i.gate])),
  description: "The company's staff, operations, connections and developer settings.",
};

export function allowed(gate: Gate, permissions: readonly string[]): boolean {
  if (gate === "anyone") return true;
  const needed = typeof gate === "string" ? [gate] : gate;
  return needed.some((p) => permissions.includes(p));
}

/** The menu a role sees: items it may open, and no empty sections. */
export function visibleMenu(menu: ConsoleSection[], permissions: readonly string[]): ConsoleSection[] {
  return menu
    .map((section) => ({ ...section, items: section.items.filter((item) => allowed(item.gate, permissions)) }))
    .filter((section) => section.items.length > 0);
}

export const toNavSections = (menu: ConsoleSection[]): NavSection[] =>
  menu.map((s) => ({ id: s.id, label: s.label, items: s.items.map(toNavItem) }));

export const toNavItem = (item: ConsoleItem): NavItem => ({
  id: item.id,
  label: item.label,
  href: item.path,
  icon: item.icon,
});

/** The item a path belongs to, with its section and menu; the longest matching path wins. */
export function itemAt(pathname: string) {
  const candidates = [
    ...MAIN_MENU.flatMap((section) => section.items.map((item) => ({ item, section, area: "main" as const }))),
    ...SETTINGS_MENU.flatMap((section) => section.items.map((item) => ({ item, section, area: "settings" as const }))),
    { item: ACCOUNT_ITEM, section: { id: "account", items: [ACCOUNT_ITEM] }, area: "main" as const },
  ];
  const matches = candidates.filter(({ item }) =>
    item.path === "/" ? pathname === "/" : pathname === item.path || pathname.startsWith(`${item.path}/`),
  );
  return matches.sort((a, b) => b.item.path.length - a.item.path.length)[0] ?? null;
}

/** The section › page (› record) crumbs for a path. With a record, the page links back to its list. */
export function breadcrumbFor(pathname: string, record?: string | null): { label: string; href?: string }[] {
  const at = itemAt(pathname);
  if (!at) return [];
  const page = record ? { label: at.item.label, href: at.item.path } : { label: at.item.label };
  const crumbs =
    at.area === "settings"
      ? [{ label: "Settings", href: "/settings" }, page]
      : at.section.label
        ? [{ label: at.section.label }, page]
        : [page];
  return record ? [...crumbs, { label: record }] : crumbs;
}
